// CHANGE: Removed all debug console statements for production
// Modal for deleting tasks
// Similar structure to ReassignTaskModal but simplified for deletion
// Supports single occurrence deletion, all future deletions, and until-date deletions for recurring tasks

import { useState, useEffect } from 'react';
import { X, Clock, AlertTriangle, CalendarCheck, CalendarRange, CalendarClock, Check, AlertCircle } from 'lucide-react';
import type { AssignedTaskWithDetails } from '../../lib/execution';
import { formatDate } from '../../lib/dateUtils';
import { DatePicker } from '../ui/DatePicker';
import { getRecurrence, getRecurrencePattern } from '../../lib/recurrence';
import type { RecurrenceRule } from '../../lib/supabase';

interface DeleteTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: AssignedTaskWithDetails | null;
  onDelete: (scope: 'single' | 'all_future' | 'until_date', untilDate?: string) => Promise<void>;
}

export function DeleteTaskModal({
  isOpen,
  onClose,
  task,
  onDelete
}: DeleteTaskModalProps) {
  const [scope, setScope] = useState<'single' | 'all_future' | 'until_date'>('single');
  const [untilDate, setUntilDate] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null);
  const [loadingRecurrence, setLoadingRecurrence] = useState(false);

  // Reset state when modal opens/closes or task changes
  useEffect(() => {
    if (isOpen && task) {
      setScope(task.is_recurring ? 'single' : 'single');
      setUntilDate('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, task]);

  // Load recurrence rule if task is recurring
  useEffect(() => {
    if (!task || !task.is_recurring || !task.template_id) {
      setRecurrenceRule(null);
      return;
    }

    let isMounted = true;

    const loadRecurrence = async () => {
      setLoadingRecurrence(true);
      try {
        const rule = await getRecurrence(task.template_id);
        if (isMounted) {
          setRecurrenceRule(rule);
        }
      } catch (error) {
      } finally {
        if (isMounted) {
          setLoadingRecurrence(false);
        }
      }
    };

    loadRecurrence();

    return () => {
      isMounted = false;
    };
  }, [task?.id, task?.is_recurring, task?.template_id]);

  // Set minimum date for until date picker (tomorrow)
  const minUntilDate = task?.assigned_date
    ? formatDate(new Date(new Date(task.assigned_date).getTime() + 86400000))
    : formatDate(new Date(Date.now() + 86400000));

  if (!isOpen || !task) return null;

  const getStatusBadge = () => {
    switch (task.status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded">
            <Check size={12} />
            Terminée
          </span>
        );
      case 'overdue':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded">
            <AlertCircle size={12} />
            En retard
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded">
            <Clock size={12} />
            Non démarrée
          </span>
        );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (scope === 'until_date' && !untilDate) {
      setError('Veuillez sélectionner une date de fin');
      return;
    }

    if (scope === 'until_date' && untilDate && task?.assigned_date && untilDate <= task.assigned_date) {
      setError('La date de fin doit être postérieure à la date de l\'occurrence');
      return;
    }

    setIsSubmitting(true);

    try {
      await onDelete(
        scope,
        scope === 'until_date' ? untilDate : undefined
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-100">
          {/* Actions in top-right */}
          <div className="flex items-center justify-end gap-2 mb-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
              title="Fermer"
            >
              <X size={20} />
            </button>
          </div>

          {/* Title full-width */}
          <h2 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">
            {task.template.name}
          </h2>

          {/* Badges aligned horizontally */}
          <div className="flex items-center gap-2 flex-wrap">
            {getStatusBadge()}
            {task.is_recurring && recurrenceRule && getRecurrencePattern(recurrenceRule) && (
              <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded">
                {getRecurrencePattern(recurrenceRule)}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <form onSubmit={handleSubmit}>
            {/* Confirmation Message */}
            <div className="mb-5">
              <p className="text-sm text-gray-700 mb-4">
                Êtes-vous sûr de vouloir supprimer cette mission ?
              </p>

              {/* Warning */}
              <div className="flex items-start gap-2 text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Cette action est irréversible</span>
              </div>
            </div>

            {/* Scope Selector (only for recurring tasks) */}
            {task.is_recurring && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Appliquer à
                </label>

                <div className="space-y-2">
                  {/* Single occurrence */}
                  <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    scope === 'single'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    <input
                      type="radio"
                      name="scope"
                      value="single"
                      checked={scope === 'single'}
                      onChange={(e) => setScope(e.target.value as 'single')}
                      disabled={isSubmitting}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarCheck size={16} className={scope === 'single' ? 'text-blue-600' : 'text-gray-400'} />
                        <span className={`text-sm font-medium ${scope === 'single' ? 'text-blue-900' : 'text-gray-900'}`}>
                          Uniquement ce jour
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDateShort(task.assigned_date)}
                      </span>
                    </div>
                  </label>

                  {/* All future */}
                  <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    scope === 'all_future'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    <input
                      type="radio"
                      name="scope"
                      value="all_future"
                      checked={scope === 'all_future'}
                      onChange={(e) => setScope(e.target.value as 'all_future')}
                      disabled={isSubmitting}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarRange size={16} className={scope === 'all_future' ? 'text-blue-600' : 'text-gray-400'} />
                        <span className={`text-sm font-medium ${scope === 'all_future' ? 'text-blue-900' : 'text-gray-900'}`}>
                          Ce jour et tous les suivants
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        À partir du {formatDateShort(task.assigned_date)}
                      </span>
                    </div>
                  </label>

                  {/* Until date */}
                  <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    scope === 'until_date'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    <input
                      type="radio"
                      name="scope"
                      value="until_date"
                      checked={scope === 'until_date'}
                      onChange={(e) => setScope(e.target.value as 'until_date')}
                      className="mt-0.5"
                      disabled={isSubmitting}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarClock size={16} className={scope === 'until_date' ? 'text-blue-600' : 'text-gray-400'} />
                        <span className={`text-sm font-medium ${scope === 'until_date' ? 'text-blue-900' : 'text-gray-900'}`}>
                          Jusqu'à une date
                        </span>
                      </div>
                      {scope === 'until_date' && (
                        <DatePicker
                          value={untilDate}
                          onChange={setUntilDate}
                          minDate={minUntilDate}
                          disabled={isSubmitting}
                          required
                          className="mt-2"
                        />
                      )}
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'En cours...' : 'Supprimer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
